import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import { StatusChip } from '../components/app/StatusChip';
import { SuggestionBadge } from '../components/app/SuggestionBadge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export default function ExceptionsPage() {
  const { activePeriod } = usePeriod();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [cat, setCat] = useState('');
  const [cc, setCC]   = useState('');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = activePeriod ? { period_id: activePeriod.id } : {};
      const res = await API.get('/exceptions', { params });
      setItems(res.data.items || []);
    } catch(e) { toast.error('Failed to load exceptions'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    API.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    API.get('/cost-centers').then(r => setCostCenters(r.data)).catch(() => {});
  }, [activePeriod?.id]);

  const openPanel = (txn) => {
    setSelectedTxn(txn);
    setCat(txn.category_id || '');
    setCC(txn.cost_center_id || '');
    setDesc(txn.description || '');
    setPanelOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/transactions/${selectedTxn.id}/allocate`, { category_id: cat || null, cost_center_id: cc || null, description: desc || null });
      toast.success('Saved');
      setPanelOpen(false);
      load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle size={20} className="text-orange-500" />
          <h1 className="text-xl font-bold">Exceptions Queue</h1>
        </div>
        <div className="text-sm text-slate-500">
          {items.length} exception{items.length !== 1 ? 's' : ''} requiring attention
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="exceptions-table">
          <thead><tr>
            <th>Status</th><th>Date</th><th>Merchant</th>
            <th className="text-right">Amount</th>
            <th>Cardholder</th><th>Category</th><th>Cost Center</th>
            <th>Suggestion</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8"><RefreshCw size={16} className="animate-spin inline text-slate-400" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400">
                <div className="flex flex-col items-center gap-2">
                  <AlertTriangle size={24} className="text-slate-300" />
                  <span>No exceptions — all transactions are coded!</span>
                </div>
              </td></tr>
            ) : items.map(t => (
              <tr key={t.id} className="cursor-pointer exception" onClick={() => openPanel(t)}>
                <td><StatusChip status={t.status} size="xs" /></td>
                <td className="text-xs">{t.date}</td>
                <td className="text-sm font-medium max-w-48 truncate" title={t.description_raw}>{t.description_raw}</td>
                <td className="text-right tabular-nums font-semibold">{t.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                <td className="text-xs">{t.cardholder_name || '—'}</td>
                <td className="text-xs">{t.category_name || <span className="text-orange-400">Uncoded</span>}</td>
                <td className="text-xs">{t.cost_center_name || '—'}</td>
                <td onClick={e => e.stopPropagation()}>
                  {t.suggestion && <SuggestionBadge suggestion={t.suggestion} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent side="right" className="w-[380px] bg-white">
          <SheetHeader><SheetTitle className="text-sm">Resolve Exception</SheetTitle></SheetHeader>
          {selectedTxn && (
            <div className="space-y-4 pt-4 pb-20 overflow-y-auto">
              <div className="border rounded p-3 space-y-1">
                <div className="text-xs text-slate-500">{selectedTxn.date}</div>
                <div className="font-semibold">{selectedTxn.description_raw}</div>
                <div className="text-xl font-bold">{selectedTxn.amount?.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' })}</div>
                <div className="flex gap-2"><StatusChip status={selectedTxn.status} size="xs" />{selectedTxn.suggestion && <SuggestionBadge suggestion={selectedTxn.suggestion} />}</div>
              </div>
              <div>
                <label className="text-xs font-medium">Category</label>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Cost Center</label>
                <Select value={cc} onValueChange={setCC}>
                  <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">{costCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Notes</label>
                <Textarea value={desc} onChange={e => setDesc(e.target.value)} className="mt-1 text-sm h-16 resize-none" />
              </div>
              <Button size="sm" className="w-full" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save & Resolve'}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
